import { sliceSafe } from "@/shared/lib/sliceSafe";

/**
 * 作品名词面 —— 两条都来自 2026-08-10 阳光400 真实数据走查。
 *
 * ① 名字里不许躺着排版:任务名直接取用户原始 prompt 的前 30 字,而 `.trim()` 只削首尾,
 *    内部换行原样进窗口。实测 29 条媒体任务里 18 条的 `name` 长这样::
 *
 *      全国统一服务热线\n让连接，更有价值\n\n▎经典之选\n4000-
 *
 *    作品列表一行放不下一段排版,渲染出来就是一团。
 *
 * ② 长图不是只有电商:活动单 / 企业单的作品名一律顶着「详情长图 · xxx」——
 *    与 corp 块名、`_USAGE_TONE_RULES` 那两课同一件事:**结构性标签也有词面纪律**。
 *    (娜娜那张活动招商长图,库里的名字是「详情长图 · 2026 企服 AI 城市合伙人闭门招商沙龙」。)
 */

/** 用途 → 长图作品名的名词。与 `USE_CASES` 同一张表的用途集,新用途两处一起加。 */
const LONGIMAGE_NOUN: Record<string, string> = {
  goods: "详情长图",
  corp: "企业长图",
  event: "活动长图",
  review: "测评长图",
  report: "战报长图",
  hire: "招聘长图",
  menu: "菜单长图",
  tutorial: "教程长图",
  rank: "榜单长图",
  social: "种草长图",
};

/** 取用户原文的前 `max` 字当作品名,并把换行/连续空白压成单空格。 */
export function flatTaskName(raw: string | null | undefined, max = 30): string {
  return sliceSafe((raw ?? "").replace(/\s+/g, " ").trim(), max);
}

/**
 * 长图作品名:`<用途词面> · <主语>`。
 *
 * 用途不明(存量草稿 / 老入口)按 goods 兜底 —— 与 `arcs.arc_of` 同一条兜底口径。
 */
export function longImageTaskName(
  useCase: string | null | undefined,
  subject: string | null | undefined,
  at?: Date
): string {
  const noun = LONGIMAGE_NOUN[useCase ?? ""] ?? LONGIMAGE_NOUN.goods;
  const flat = flatTaskName(subject, 60);
  // 1005 生产验收:模板路没有主语,作品里一排「企业长图」「企业长图」分不清 ——
  // 给了时间就补「MM-DD HH:mm」当区分(调用方传 new Date())。
  const tail = flat || (at ? stamp(at) : "");
  return (tail ? `${noun} · ${tail}` : noun).slice(0, 60);
}

/** 作品名用的短时间戳「10-05 21:49」(本地时区)。 */
export function stamp(at: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(at.getMonth() + 1)}-${p(at.getDate())} ${p(at.getHours())}:${p(at.getMinutes())}`;
}

/** 文案第一段「标题」的切分符:冒号 / 句末标点 / 分号 / 换行。逗号不切(太碎)。 */
const HEAD_SPLIT = /[:：。;；!！?？\n]/;
/** 指令口吻开头 —— 这种段是在吩咐模型,不是作品的名字。 */
const INSTRUCTION_HEAD = /^(请|以|把|将|用|让|保留|根据|参考|按照|按|基于|围绕|生成|做一张|画一张|帮我)/;

/**
 * 从用户文案里取「标题段」:第一个冒号/句号/分号前的那一段,4~30 字且不是指令口吻才用。
 *
 * 1005 生产验收:「商标担保注册套餐电商主图:以上传的商标注册证图为主体,白底干…」整段
 * 前 30 字当了作品名。用户自己写在冒号前的就是名字。
 */
export function promptHead(prompt: string | null | undefined): string {
  const flat = (prompt ?? "").trim();
  if (!flat) return "";
  const head = flatTaskName(flat.split(HEAD_SPLIT)[0], 40);
  if ([...head].length < 4 || [...head].length > 30) return "";
  if (INSTRUCTION_HEAD.test(head)) return "";
  return head;
}

/**
 * 海报作品名(1004 T8)。优先级:**产品名**(可加卡名前缀)> 用户文案 > 兜底。
 *
 * ★此前直接取 prompt 前 30 字。可场景卡 / 改图卡的 prompt 是一段**指令模板**(「保留画面里的
 *   主体商品:轮廓、材质…」),作品列表、下载文件名全成了「保留画面里的主体商品…」—— 十张
 *   作品一个名字,找不回来。产品名是用户自己挑的、能认出来的那个词,排第一。
 * ★卡名(sceneTitle)只作前缀:「商品主图 · 囍上眉梢·婚嫁系列」,没有产品时不单用卡名
 *   (十张都叫「商品主图」等于没起名),退回文案。
 */
export function posterTaskName(args: {
  productName?: string | null;
  sceneTitle?: string | null;
  prompt?: string | null;
  fallback: string;
  /** 给了就在「整段是指令」时用「卡名 · 时间」当名字(1005)。 */
  at?: Date;
}): string {
  const product = flatTaskName(args.productName, 40);
  const scene = flatTaskName(args.sceneTitle, 12);
  if (product) {
    return sliceSafe(scene ? `${scene} · ${product}` : product, 60);
  }
  // 1005:没产品时先取文案的标题段(冒号前那句),再不行才退回前 30 字。
  const head = promptHead(args.prompt);
  if (head) return head;
  // 文案整段是指令(「保留画面里的主体商品:…」)→ 卡名 · 时间,比一段指令好认。
  if (scene && args.at && INSTRUCTION_HEAD.test(flatTaskName(args.prompt))) {
    return `${scene} · ${stamp(args.at)}`;
  }
  return flatTaskName(args.prompt) || args.fallback;
}
